import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DENTAL_SPECIALTIES } from '../lib/constants';
import { supabase } from '../lib/supabaseClient';

/**
 * ConsumableWarehouseReportModal
 * Official Report Generator for Main Warehouse (คลังใหญ่) & Sub-Warehouse (คลังย่อย)
 *
 * Props:
 * - isOpen: boolean
 * - onClose: func
 * - itemMaster: array of master items
 * - supplies: array of supply items
 * - logs: array of activity / audit logs
 * - initialWarehouse: string ('คลังใหญ่ (คลังพัสดุ)' | 'คลังย่อย (คลังห้องฟัน)' | 'ทั้งสองคลัง')
 * - user: current user object
 * - consumableItems: optional pre-loaded QR-tagged consumable items array
 */
const ConsumableWarehouseReportModal = ({
  isOpen = false,
  onClose = () => {},
  itemMaster = [],
  supplies = [],
  logs = [],
  initialWarehouse = 'คลังใหญ่ (คลังพัสดุ)',
  user = {},
  consumableItems = []
}) => {
  // 1. UI Control States
  const [selectedWarehouse, setSelectedWarehouse] = useState(initialWarehouse);
  const [reportType, setReportType] = useState('balance'); // 'balance' | 'movement' | 'sub_qr'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [periodType, setPeriodType] = useState('current_month'); // 'all' | 'current_month' | 'fiscal_year' | 'custom'

  // Custom date range states (YYYY-MM-DD)
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const firstOfMonthStr = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  }, []);
  const [customStartDate, setCustomStartDate] = useState(firstOfMonthStr);
  const [customEndDate, setCustomEndDate] = useState(todayStr);

  // Dynamic QR items from database or local storage
  const [dynamicQrItems, setDynamicQrItems] = useState([]);
  const [isLoadingQr, setIsLoadingQr] = useState(false);

  // Sync initial warehouse when modal opens or initialWarehouse prop changes
  useEffect(() => {
    if (isOpen) {
      setSelectedWarehouse(initialWarehouse || 'คลังใหญ่ (คลังพัสดุ)');
    }
  }, [isOpen, initialWarehouse]);

  // Fetch consumable_items when modal opens or report type is 'sub_qr'
  useEffect(() => {
    if (!isOpen) return;

    if (Array.isArray(consumableItems) && consumableItems.length > 0) {
      setDynamicQrItems(consumableItems);
      return;
    }

    let isMounted = true;
    const fetchQrItems = async () => {
      setIsLoadingQr(true);
      try {
        if (supabase) {
          const { data, error } = await supabase
            .from('consumable_items')
            .select('*')
            .order('created_at', { ascending: false });

          if (!error && Array.isArray(data) && data.length > 0 && isMounted) {
            setDynamicQrItems(data);
            setIsLoadingQr(false);
            return;
          }
        }

        // Fallback: check localStorage transfers for qr_codes
        if (typeof window !== 'undefined' && window.localStorage) {
          const rawTransfers = window.localStorage.getItem('dental_consumable_transfers');
          const transfers = rawTransfers ? JSON.parse(rawTransfers) : [];
          const localQrItems = [];

          transfers.forEach((t) => {
            const qrs = Array.isArray(t.qr_codes) ? t.qr_codes : [];
            qrs.forEach((code) => {
              localQrItems.push({
                qr_code: code,
                item_name: t.item_name || 'วัสดุสิ้นเปลือง',
                lot_number: t.lot_number || '-',
                exp_date: t.exp_date || null,
                unit: t.unit || 'ชิ้น',
                current_location: t.destination_warehouse || 'คลังย่อย (คลังห้องฟัน)',
                status: 'active',
                caretaker_name: t.transferred_by || 'ผู้ดูแลคลังย่อย',
                created_at: t.transferred_at || new Date().toISOString()
              });
            });
          });

          if (localQrItems.length > 0 && isMounted) {
            setDynamicQrItems(localQrItems);
            setIsLoadingQr(false);
            return;
          }
        }

        // Fallback: synthesize items from sub-warehouse supplies if none found
        if (isMounted) {
          const synth = [];
          const subSupplies = (supplies || []).filter(
            (s) => s.warehouse_name && s.warehouse_name.includes('คลังย่อย')
          );

          subSupplies.forEach((s, idx) => {
            const master = (itemMaster || []).find((m) => String(m.id) === String(s.master_id));
            if (master) {
              const count = Math.min(Number(s.item_amount) || 1, 5);
              for (let i = 0; i < count; i++) {
                synth.push({
                  id: `synth-${idx}-${i}`,
                  qr_code: `CON-${(master.lot_number || '2601').replace(/[^a-zA-Z0-9]/g, '')}-${String(idx + 1).padStart(3, '0')}${String(i + 1).padStart(2, '0')}`,
                  item_name: master.item_name,
                  specialty: master.specialty || 'งานอุดฟัน',
                  lot_number: master.lot_number || '-',
                  exp_date: master.exp_date || null,
                  unit: master.unit || 'ชิ้น',
                  unit_price: Number(master.unit_price) || 0,
                  total_price: Number(master.unit_price) || 0,
                  current_location: 'คลังย่อย (คลังห้องฟัน)',
                  status: 'active',
                  caretaker_name: 'ผู้ดูแลคลังย่อย',
                  created_at: new Date().toISOString()
                });
              }
            }
          });
          setDynamicQrItems(synth);
        }
      } catch (err) {
        console.warn('Error retrieving QR items for report:', err);
      } finally {
        if (isMounted) setIsLoadingQr(false);
      }
    };

    fetchQrItems();
    return () => {
      isMounted = false;
    };
  }, [isOpen, consumableItems, supplies, itemMaster]);

  // Helper: Warehouse name matching
  const isMatchWarehouse = useCallback((targetWh, candidateWh) => {
    if (!candidateWh) return false;
    const t = String(targetWh || '').trim();
    const c = String(candidateWh || '').trim();
    if (t === 'ทั้งสองคลัง') {
      return c.includes('คลังใหญ่') || c.includes('คลังย่อย');
    }
    if (t.includes('คลังใหญ่')) return c.includes('คลังใหญ่');
    if (t.includes('คลังย่อย')) return c.includes('คลังย่อย');
    return c.includes(t);
  }, []);

  // Helper: Period date range calculation
  const dateRange = useMemo(() => {
    const now = new Date();
    if (periodType === 'all') {
      return { start: null, end: null, label: 'ข้อมูลทั้งหมด (All Time)' };
    }
    if (periodType === 'current_month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      const thMonth = start.toLocaleDateString('th-TH', { month: 'long', year: 'numeric' });
      return { start, end, label: `เดือน${thMonth}` };
    }
    if (periodType === 'fiscal_year') {
      const curMonth = now.getMonth(); // 0 = Jan, 9 = Oct
      const curYear = now.getFullYear();
      let fyStart, fyEnd, fyBeYear;
      if (curMonth >= 9) {
        fyStart = new Date(curYear, 9, 1, 0, 0, 0);
        fyEnd = new Date(curYear + 1, 8, 30, 23, 59, 59);
        fyBeYear = curYear + 1 + 543;
      } else {
        fyStart = new Date(curYear - 1, 9, 1, 0, 0, 0);
        fyEnd = new Date(curYear, 8, 30, 23, 59, 59);
        fyBeYear = curYear + 543;
      }
      return {
        start: fyStart,
        end: fyEnd,
        label: `ปีงบประมาณ ${fyBeYear} (1 ต.ค. ${fyStart.getFullYear() + 543} - 30 ก.ย. ${fyEnd.getFullYear() + 543})`
      };
    }
    if (periodType === 'custom') {
      const start = customStartDate ? new Date(customStartDate + 'T00:00:00') : null;
      const end = customEndDate ? new Date(customEndDate + 'T23:59:59') : null;
      return {
        start,
        end,
        label: `${customStartDate ? new Date(customStartDate).toLocaleDateString('th-TH') : '-'} ถึง ${customEndDate ? new Date(customEndDate).toLocaleDateString('th-TH') : '-'}`
      };
    }
    return { start: null, end: null, label: '-' };
  }, [periodType, customStartDate, customEndDate]);

  // Filter only consumable items
  const consumableMasters = useMemo(() => {
    return (itemMaster || []).filter(
      (m) => m && (m.category === 'วัสดุสิ้นเปลือง' || !m.category) && !m.is_set
    );
  }, [itemMaster]);

  // Map supplies by master_id
  const suppliesByMaster = useMemo(() => {
    const map = new Map();
    (supplies || []).forEach((s) => {
      if (!s || s.master_id === undefined || s.master_id === null) return;
      if (!map.has(s.master_id)) {
        map.set(s.master_id, []);
      }
      map.get(s.master_id).push(s);
    });
    return map;
  }, [supplies]);

  // Master lookup map by id
  const masterById = useMemo(() => {
    const map = new Map();
    (itemMaster || []).forEach((m) => {
      if (m && m.id) map.set(String(m.id), m);
    });
    return map;
  }, [itemMaster]);

  // ============================================================================
  // REPORT DATA 1: Inventory Balance & Valuation (รายงานสรุปยอดคงคลัง)
  // ============================================================================
  const balanceReportData = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    const today = new Date();
    const rows = [];

    let sumQty = 0;
    let sumValuation = 0;
    let expiredLots = 0;
    let expiringLots = 0;
    let normalLots = 0;

    consumableMasters.forEach((lot) => {
      // Filter by specialty if selected
      if (selectedSpecialty !== 'all') {
        const lotSpec = lot.specialty || 'ทันตกรรมทั่วไป/อื่นๆ';
        if (!lotSpec.includes(selectedSpecialty) && lotSpec !== selectedSpecialty) {
          return;
        }
      }

      // Filter by search query
      if (q) {
        const matchName = (lot.item_name || '').toLowerCase().includes(q);
        const matchLot = (lot.lot_number || '').toLowerCase().includes(q);
        const matchAlias = (lot.aliases || '').toLowerCase().includes(q);
        const matchLocation = (lot.location || '').toLowerCase().includes(q);
        if (!matchName && !matchLot && !matchAlias && !matchLocation) {
          return;
        }
      }

      const lotSupplies = suppliesByMaster.get(lot.id) || [];
      const matchingSupplies = lotSupplies.filter((s) =>
        isMatchWarehouse(selectedWarehouse, s.warehouse_name)
      );

      const qty = matchingSupplies.reduce((acc, s) => acc + (Number(s.item_amount) || 0), 0);

      // Only include lots that either have stock or are tracked
      const unitPrice = Number(lot.unit_price) || 0;
      const totalValuation = qty * unitPrice;

      // Expiration & Safety calculation
      let statusLabel = 'ปกติ';
      let statusColor = '#10b981'; // green
      let statusBg = 'rgba(16, 185, 129, 0.15)';

      if (qty <= 0) {
        statusLabel = 'หมด';
        statusColor = '#94a3b8';
        statusBg = 'rgba(148, 163, 184, 0.15)';
      } else if (lot.exp_date && lot.exp_date !== '9999-12-31') {
        const expDateObj = new Date(lot.exp_date);
        const diffMonths = (expDateObj - today) / (1000 * 60 * 60 * 24 * 30);

        if (expDateObj <= today) {
          statusLabel = 'หมดอายุ';
          statusColor = '#ef4444';
          statusBg = 'rgba(239, 68, 68, 0.18)';
          expiredLots += 1;
        } else if (diffMonths < 3) {
          statusLabel = '< 3 ด. (ใกล้หมด)';
          statusColor = '#f97316';
          statusBg = 'rgba(249, 115, 22, 0.18)';
          expiringLots += 1;
        } else if (diffMonths <= 12) {
          statusLabel = '3ด.-1ปี';
          statusColor = '#eab308';
          statusBg = 'rgba(234, 179, 8, 0.18)';
          expiringLots += 1;
        } else {
          statusLabel = 'ปกติ';
          normalLots += 1;
        }
      } else {
        normalLots += 1;
      }

      const minStock = Number(lot.min_stock) || 0;
      const isBelowMin = qty > 0 && minStock > 0 && qty < minStock;

      sumQty += qty;
      sumValuation += totalValuation;

      rows.push({
        id: lot.id,
        item_name: lot.item_name || 'ไม่ระบุชื่อ',
        lot_number: lot.lot_number || '-',
        exp_date: lot.exp_date || '-',
        quantity: qty,
        unit: lot.unit || 'ชิ้น',
        unit_price: unitPrice,
        total_valuation: totalValuation,
        min_stock: minStock,
        isBelowMin,
        statusLabel,
        statusColor,
        statusBg,
        specialty: lot.specialty || 'ทันตกรรมทั่วไป',
        location: [lot.cabinet && `ตู้ ${lot.cabinet}`, lot.shelf && `ชั้น ${lot.shelf}`, lot.row && `แถว ${lot.row}`]
          .filter(Boolean)
          .join(' ') || '-'
      });
    });

    // Sort by expiration date ascending, then name
    rows.sort((a, b) => {
      if (!a.exp_date || a.exp_date === '-') return 1;
      if (!b.exp_date || b.exp_date === '-') return -1;
      return new Date(a.exp_date) - new Date(b.exp_date);
    });

    return {
      rows,
      sumQty,
      sumValuation,
      totalSKUs: rows.length,
      expiredLots,
      expiringLots,
      normalLots
    };
  }, [
    consumableMasters,
    suppliesByMaster,
    searchQuery,
    selectedSpecialty,
    selectedWarehouse,
    isMatchWarehouse
  ]);

  // ============================================================================
  // REPORT DATA 2: Stock Movement & Audit (รายงานความเคลื่อนไหว)
  // ============================================================================
  const movementReportData = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    const rows = [];

    let inCount = 0;
    let outCount = 0;
    let transferCount = 0;
    let otherCount = 0;

    (logs || []).forEach((log, idx) => {
      if (!log || !log.action) return;
      const act = String(log.action);
      const wh = String(log.warehouse || '');

      // 1. Warehouse Match
      const matchesWh =
        isMatchWarehouse(selectedWarehouse, wh) ||
        (selectedWarehouse.includes('คลังใหญ่') && act.includes('คลังใหญ่')) ||
        (selectedWarehouse.includes('คลังย่อย') && act.includes('คลังย่อย')) ||
        selectedWarehouse === 'ทั้งสองคลัง';

      if (!matchesWh) return;

      // 2. Date Range Match
      if (log.timestamp) {
        const logDate = new Date(log.timestamp);
        if (dateRange.start && logDate < dateRange.start) return;
        if (dateRange.end && logDate > dateRange.end) return;
      }

      // 3. Search Query Match
      if (q) {
        const matchItem = (log.item_name || '').toLowerCase().includes(q);
        const matchAct = act.toLowerCase().includes(q);
        const matchWh = wh.toLowerCase().includes(q);
        const matchActor = (log.actor || log.user || log.created_by || '').toLowerCase().includes(q);
        if (!matchItem && !matchAct && !matchWh && !matchActor) return;
      }

      // 4. Specialty Filter
      if (selectedSpecialty !== 'all') {
        const itemObj = masterById.get(String(log.item_id || log.master_id));
        const spec = itemObj?.specialty || '';
        if (spec && !spec.includes(selectedSpecialty)) return;
      }

      // Determine Movement Type
      let typeLabel = 'ความเคลื่อนไหว';
      let typeColor = '#3b82f6';
      let typeBg = 'rgba(59, 130, 246, 0.15)';

      if (act.includes('รับเข้า') || act.includes('ตรวจรับ') || act.includes('เพิ่มสต็อก')) {
        typeLabel = '📥 รับเข้า (Stock In)';
        typeColor = '#10b981';
        typeBg = 'rgba(16, 185, 129, 0.15)';
        inCount += 1;
      } else if (act.includes('โอนย้าย') || act.includes('โอน')) {
        typeLabel = '🔄 โอนย้าย (Transfer)';
        typeColor = '#00bcd4';
        typeBg = 'rgba(0, 188, 212, 0.15)';
        transferCount += 1;
      } else if (act.includes('เบิก') || act.includes('จ่าย') || act.includes('ตัดใช้')) {
        typeLabel = '📤 เบิกจ่าย (Dispense)';
        typeColor = '#f59e0b';
        typeBg = 'rgba(245, 158, 11, 0.15)';
        outCount += 1;
      } else {
        otherCount += 1;
      }

      // Extract Actor
      let actor = log.user || log.actor || log.created_by || '-';
      if (actor === '-' && act.includes('โดย')) {
        const match = act.match(/โดย\s+([^\s,()]+)/);
        if (match) actor = match[1];
      }

      rows.push({
        id: log.id || `mov-${idx}`,
        timestamp: log.timestamp || log.created_at || null,
        item_name: log.item_name || 'วัสดุสิ้นเปลือง',
        action: act,
        warehouse: wh || selectedWarehouse,
        actor,
        typeLabel,
        typeColor,
        typeBg
      });
    });

    // Sort by timestamp desc
    rows.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));

    return {
      rows,
      totalMovements: rows.length,
      inCount,
      outCount,
      transferCount,
      otherCount
    };
  }, [
    logs,
    selectedWarehouse,
    isMatchWarehouse,
    dateRange,
    searchQuery,
    selectedSpecialty,
    masterById
  ]);

  // ============================================================================
  // REPORT DATA 3: Sub-Warehouse QR Tag Status (รายงานการติดแท็ก QR คลังย่อย)
  // ============================================================================
  const subQrReportData = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    const rows = [];

    let totalTaggedValuation = 0;
    const uniqueItemNames = new Set();

    (dynamicQrItems || []).forEach((item, idx) => {
      // Only items that are in sub-warehouse (or active)
      const loc = item.current_location || item.warehouse_name || '';
      const isSub =
        selectedWarehouse === 'ทั้งสองคลัง' ||
        selectedWarehouse.includes('คลังย่อย') ||
        loc.includes('คลังย่อย') ||
        !loc;

      if (!isSub) return;

      // Filter by specialty
      if (selectedSpecialty !== 'all') {
        const spec = item.specialty || '';
        if (spec && !spec.includes(selectedSpecialty) && spec !== selectedSpecialty) return;
      }

      // Filter by search query
      if (q) {
        const matchQr = (item.qr_code || '').toLowerCase().includes(q);
        const matchName = (item.item_name || '').toLowerCase().includes(q);
        const matchLot = (item.lot_number || '').toLowerCase().includes(q);
        const matchCare = (item.caretaker_name || '').toLowerCase().includes(q);
        if (!matchQr && !matchName && !matchLot && !matchCare) return;
      }

      const unitPrice = Number(item.unit_price) || 0;
      totalTaggedValuation += unitPrice;
      if (item.item_name) uniqueItemNames.add(item.item_name);

      rows.push({
        id: item.id || `qr-${idx}`,
        qr_code: item.qr_code || `CON-${idx + 1}`,
        item_name: item.item_name || 'วัสดุสิ้นเปลือง',
        lot_number: item.lot_number || '-',
        specialty: item.specialty || 'ทันตกรรมทั่วไป',
        exp_date: item.exp_date || '-',
        unit: item.unit || 'ชิ้น',
        unit_price: unitPrice,
        status: item.status || 'active',
        current_location: item.current_location || 'คลังย่อย (คลังห้องฟัน)',
        caretaker_name: item.caretaker_name || item.created_by || 'ผู้ดูแลคลัง',
        created_at: item.created_at || item.last_scanned_at || null
      });
    });

    return {
      rows,
      totalTags: rows.length,
      uniqueSKUs: uniqueItemNames.size,
      totalTaggedValuation
    };
  }, [dynamicQrItems, selectedWarehouse, selectedSpecialty, searchQuery]);

  // ============================================================================
  // ACTION: Window Print Trigger
  // ============================================================================
  const handlePrint = () => {
    window.print();
  };

  // ============================================================================
  // ACTION: Export to CSV with UTF-8 BOM
  // ============================================================================
  const handleExportCsv = () => {
    let csvContent = '\uFEFF'; // UTF-8 BOM
    const timestampStr = new Date().toISOString().slice(0, 10);
    const whSlug = selectedWarehouse.includes('คลังใหญ่')
      ? 'main_wh'
      : selectedWarehouse.includes('คลังย่อย')
      ? 'sub_wh'
      : 'both_wh';

    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).trim();
      return `"${str.replace(/"/g, '""')}"`;
    };

    if (reportType === 'balance') {
      // CSV Headers
      csvContent += [
        'ลำดับ',
        'ชื่อวัสดุสิ้นเปลือง',
        'สาขาทันตกรรม',
        'หมายเลขล็อต (Lot)',
        'วันหมดอายุ (EXP)',
        'จำนวนคงเหลือ',
        'หน่วยนับ',
        'ราคาต่อหน่วย (บาท)',
        'มูลค่ารวม (บาท)',
        'เกณฑ์ขั้นต่ำ',
        'สถานะความปลอดภัย',
        'ตำแหน่งจัดเก็บ'
      ].map(escapeCsv).join(',') + '\r\n';

      // CSV Rows
      balanceReportData.rows.forEach((r, idx) => {
        csvContent += [
          idx + 1,
          r.item_name,
          r.specialty,
          r.lot_number,
          r.exp_date,
          r.quantity,
          r.unit,
          r.unit_price.toFixed(2),
          r.total_valuation.toFixed(2),
          r.min_stock,
          r.statusLabel,
          r.location
        ].map(escapeCsv).join(',') + '\r\n';
      });

      // CSV Totals Row
      csvContent += [
        'รวมทั้งสิ้น',
        `${balanceReportData.totalSKUs} รายการ`,
        '',
        '',
        '',
        balanceReportData.sumQty,
        'ชิ้น',
        '',
        balanceReportData.sumValuation.toFixed(2),
        '',
        '',
        ''
      ].map(escapeCsv).join(',') + '\r\n';

    } else if (reportType === 'movement') {
      // CSV Headers
      csvContent += [
        'ลำดับ',
        'วันที่-เวลา',
        'ประเภทรายการ',
        'ชื่อวัสดุ',
        'คลัง/สถานที่',
        'รายละเอียดรายการ',
        'ผู้ทำรายการ/ผู้อนุมัติ'
      ].map(escapeCsv).join(',') + '\r\n';

      // CSV Rows
      movementReportData.rows.forEach((r, idx) => {
        csvContent += [
          idx + 1,
          r.timestamp ? new Date(r.timestamp).toLocaleString('th-TH') : '-',
          r.typeLabel.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim(),
          r.item_name,
          r.warehouse,
          r.action,
          r.actor
        ].map(escapeCsv).join(',') + '\r\n';
      });

      // CSV Summary Row
      csvContent += [
        'สรุปยอด',
        `รวม ${movementReportData.totalMovements} รายการ`,
        `รับเข้า ${movementReportData.inCount} / โอน ${movementReportData.transferCount} / เบิก ${movementReportData.outCount}`,
        '',
        '',
        '',
        ''
      ].map(escapeCsv).join(',') + '\r\n';

    } else if (reportType === 'sub_qr') {
      // CSV Headers
      csvContent += [
        'ลำดับ',
        'รหัส QR Code (CON-)',
        'ชื่อรายการวัสดุ',
        'หมายเลขล็อต (Lot)',
        'สาขาทันตกรรม',
        'วันหมดอายุ (EXP)',
        'ราคาต่อหน่วย (บาท)',
        'สถานะ',
        'สถานที่จัดเก็บ',
        'ผู้ดูแลคลัง/ผู้ติดแท็ก',
        'วันที่บันทึก'
      ].map(escapeCsv).join(',') + '\r\n';

      // CSV Rows
      subQrReportData.rows.forEach((r, idx) => {
        csvContent += [
          idx + 1,
          r.qr_code,
          r.item_name,
          r.lot_number,
          r.specialty,
          r.exp_date,
          r.unit_price.toFixed(2),
          r.status,
          r.current_location,
          r.caretaker_name,
          r.created_at ? new Date(r.created_at).toLocaleString('th-TH') : '-'
        ].map(escapeCsv).join(',') + '\r\n';
      });

      // CSV Totals Row
      csvContent += [
        'รวมทั้งสิ้น',
        `${subQrReportData.totalTags} รหัสแท็ก`,
        `${subQrReportData.uniqueSKUs} รายการไม่ซ้ำ`,
        '',
        '',
        '',
        subQrReportData.totalTaggedValuation.toFixed(2),
        '',
        '',
        '',
        ''
      ].map(escapeCsv).join(',') + '\r\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `consumable_report_${reportType}_${whSlug}_${timestampStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div
      className="consumable-report-modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(10, 15, 29, 0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* CSS Rules for Clean A4 Hospital Document Printing */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #consumable-printable-document, #consumable-printable-document * {
            visibility: visible !important;
          }
          #consumable-printable-document {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 10mm !important;
            background: #ffffff !important;
            color: #000000 !important;
            box-shadow: none !important;
            font-size: 10pt !important;
          }
          .no-print {
            display: none !important;
          }
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          .print-break-inside-avoid {
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div
        className="consumable-report-modal-content"
        style={{
          background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '1200px',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
          overflow: 'hidden',
          color: '#ffffff'
        }}
      >
        {/* Modal Top Bar (Controls & Actions) */}
        <div
          className="no-print"
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            background: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}
        >
          {/* Header row with Title & Close button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.6em' }}>📑</span>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: '800', color: '#38bdf8' }}>
                  ศูนย์เอกสารและออกรายงานทางการ (คลังใหญ่ / คลังย่อย)
                </h3>
                <span style={{ fontSize: '0.82em', color: 'rgba(255, 255, 255, 0.6)' }}>
                  พิมพ์รายงาน A4 ทันตกรรม หรือส่งออกเป็นไฟล์ CSV (UTF-8 BOM สำหรับ Excel ภาษาไทย)
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handlePrint}
                style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  color: '#ffffff',
                  border: '1px solid rgba(56, 189, 248, 0.4)',
                  borderRadius: '10px',
                  padding: '8px 16px',
                  fontSize: '0.88em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.35)',
                  transition: 'all 0.2s ease'
                }}
              >
                🖨️ พิมพ์เอกสาร A4 (Print)
              </button>

              <button
                type="button"
                onClick={handleExportCsv}
                style={{
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: '#ffffff',
                  border: '1px solid rgba(52, 211, 153, 0.4)',
                  borderRadius: '10px',
                  padding: '8px 16px',
                  fontSize: '0.88em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
                  transition: 'all 0.2s ease'
                }}
              >
                📥 ส่งออก CSV (Excel)
              </button>

              <button
                type="button"
                onClick={onClose}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#ffffff',
                  borderRadius: '10px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '1.1em'
                }}
                title="ปิดหน้าต่าง"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Controls Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '12px',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.06)'
            }}
          >
            {/* Control 1: Warehouse Selector */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78em', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px', fontWeight: '600' }}>
                🏢 เลือกคลังเป้าหมาย:
              </label>
              <select
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                style={{
                  width: '100%',
                  background: '#0f172a',
                  color: '#ffffff',
                  border: '1px solid rgba(56, 189, 248, 0.4)',
                  borderRadius: '8px',
                  padding: '7px 10px',
                  fontSize: '0.85em',
                  fontWeight: '600'
                }}
              >
                <option value="คลังใหญ่ (คลังพัสดุ)">🏢 คลังใหญ่ (คลังพัสดุ)</option>
                <option value="คลังย่อย (คลังห้องฟัน)">🏬 คลังย่อย (คลังห้องฟัน)</option>
                <option value="ทั้งสองคลัง">🏢🏬 ทั้งสองคลัง (คลังใหญ่ & คลังย่อย)</option>
              </select>
            </div>

            {/* Control 2: Report Type Selector */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78em', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px', fontWeight: '600' }}>
                📋 รูปแบบรายงาน:
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                style={{
                  width: '100%',
                  background: '#0f172a',
                  color: '#ffffff',
                  border: '1px solid rgba(56, 189, 248, 0.4)',
                  borderRadius: '8px',
                  padding: '7px 10px',
                  fontSize: '0.85em',
                  fontWeight: '600'
                }}
              >
                <option value="balance">1. รายงานสรุปยอดคงคลัง (Balance & Valuation)</option>
                <option value="movement">2. รายงานความเคลื่อนไหว (Movement & Audit)</option>
                <option value="sub_qr">3. รายงานการติดแท็ก QR Code คลังย่อย (Sub-Warehouse QR)</option>
              </select>
            </div>

            {/* Control 3: Specialty Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78em', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px', fontWeight: '600' }}>
                🦷 สาขาทันตกรรม:
              </label>
              <select
                value={selectedSpecialty}
                onChange={(e) => setSelectedSpecialty(e.target.value)}
                style={{
                  width: '100%',
                  background: '#0f172a',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  padding: '7px 10px',
                  fontSize: '0.85em'
                }}
              >
                <option value="all">ทุกสาขาทันตกรรม (All)</option>
                {DENTAL_SPECIALTIES.map((sp) => (
                  <option key={sp.id} value={sp.name}>
                    {sp.name} ({sp.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Control 4: Period / Date Filter */}
            <div>
              <label style={{ display: 'block', fontSize: '0.78em', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px', fontWeight: '600' }}>
                📅 รอบช่วงเวลา:
              </label>
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                style={{
                  width: '100%',
                  background: '#0f172a',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  padding: '7px 10px',
                  fontSize: '0.85em'
                }}
              >
                <option value="current_month">เดือนปัจจุบัน</option>
                <option value="fiscal_year">ปีงบประมาณ</option>
                <option value="custom">กำหนดเอง (Custom)</option>
                <option value="all">ทั้งหมด (All Time)</option>
              </select>
            </div>
          </div>

          {/* Second Row of Controls: Search query & Custom Date Pickers */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <div style={{ flex: '1 1 240px', position: 'relative' }}>
              <input
                type="text"
                placeholder="🔍 ค้นหาชื่อวัสดุ, ล็อต, ตู้จัดเก็บ, รหัส QR..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  padding: '7px 12px',
                  color: '#ffffff',
                  fontSize: '0.85em',
                  boxSizing: 'border-box'
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.5)',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {periodType === 'custom' && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8em', color: 'rgba(255, 255, 255, 0.6)' }}>จาก:</span>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    background: '#0f172a',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#ffffff',
                    padding: '5px 8px',
                    fontSize: '0.82em'
                  }}
                />
                <span style={{ fontSize: '0.8em', color: 'rgba(255, 255, 255, 0.6)' }}>ถึง:</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    background: '#0f172a',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: '#ffffff',
                    padding: '5px 8px',
                    fontSize: '0.82em'
                  }}
                />
              </div>
            )}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span
                style={{
                  fontSize: '0.78em',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  background: 'rgba(56, 189, 248, 0.15)',
                  color: '#38bdf8',
                  border: '1px solid rgba(56, 189, 248, 0.3)'
                }}
              >
                รอบข้อมูล: {dateRange.label}
              </span>
            </div>
          </div>
        </div>

        {/* Modal Body: Printable Document Container */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
            background: '#0b1120'
          }}
        >
          {/* Official Document Sheet */}
          <div
            id="consumable-printable-document"
            style={{
              background: '#ffffff',
              color: '#0f172a',
              borderRadius: '8px',
              padding: '28px 32px',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.45)',
              fontFamily: "'Sarabun', 'Segoe UI', Tahoma, sans-serif",
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}
          >
            {/* 1. Official Hospital Document Header */}
            <div
              style={{
                borderBottom: '2px solid #0284c7',
                paddingBottom: '14px',
                marginBottom: '18px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}
            >
              <div>
                <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#0f172a', letterSpacing: '0.2px' }}>
                  โรงพยาบาลทันตกรรม คณะทันตแพทยศาสตร์
                </div>
                <div style={{ fontSize: '1.05em', fontWeight: '700', color: '#0369a1', marginTop: '3px' }}>
                  {reportType === 'balance' && `รายงานสรุปยอดคงคลังและมูลค่าวัสดุสิ้นเปลือง (${selectedWarehouse})`}
                  {reportType === 'movement' && `รายงานความเคลื่อนไหวและประวัติการตรวจสอบ (${selectedWarehouse})`}
                  {reportType === 'sub_qr' && `รายงานสถานะการติดแท็ก QR Code วัสดุสิ้นเปลือง (คลังย่อยห้องฟัน)`}
                </div>
                <div style={{ fontSize: '0.82em', color: '#475569', marginTop: '4px' }}>
                  Dental Hospital Consumable Inventory & Material Valuation Official Report
                </div>
              </div>

              <div style={{ textAlign: 'right', fontSize: '0.8em', color: '#475569', lineHeight: '1.4' }}>
                <div><strong>วันที่พิมพ์:</strong> {new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                <div><strong>ผู้จัดทำ:</strong> {user?.email || 'เจ้าหน้าที่งานพัสดุ / ผู้ดูแลคลัง'}</div>
                <div><strong>ช่วงเวลาข้อมูล:</strong> {dateRange.label}</div>
                {selectedSpecialty !== 'all' && (
                  <div><strong>จำแนกตามสาขา:</strong> {selectedSpecialty}</div>
                )}
              </div>
            </div>

            {/* 2. KPI Summary Bar (In Document) */}
            <div
              className="print-break-inside-avoid"
              style={{
                display: 'grid',
                gridTemplateColumns: reportType === 'balance' ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
                gap: '10px',
                marginBottom: '16px',
                padding: '10px 14px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}
            >
              {reportType === 'balance' && (
                <>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>จำนวนรายการ (SKUs)</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#0284c7' }}>
                      {balanceReportData.totalSKUs} <span style={{ fontSize: '0.65em', fontWeight: 'normal' }}>รายการ</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>จำนวนชิ้นคงเหลือรวม</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#059669' }}>
                      {balanceReportData.sumQty.toLocaleString()} <span style={{ fontSize: '0.65em', fontWeight: 'normal' }}>หน่วย</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>มูลค่ารวมทั้งสิ้น</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#d97706' }}>
                      ฿{balanceReportData.sumValuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>หมดอายุ / ใกล้หมด (3ด.)</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#dc2626' }}>
                      {balanceReportData.expiredLots + balanceReportData.expiringLots} <span style={{ fontSize: '0.65em', fontWeight: 'normal' }}>ล็อต</span>
                    </div>
                  </div>
                </>
              )}

              {reportType === 'movement' && (
                <>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>ความเคลื่อนไหวทั้งหมด</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#0284c7' }}>
                      {movementReportData.totalMovements} <span style={{ fontSize: '0.65em', fontWeight: 'normal' }}>รายการ</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>รับเข้าตามใบเสร็จ</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#059669' }}>
                      {movementReportData.inCount} <span style={{ fontSize: '0.65em', fontWeight: 'normal' }}>ครั้ง</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>โอนย้าย / เบิกจ่าย</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#d97706' }}>
                      {movementReportData.transferCount} โอน / {movementReportData.outCount} เบิก
                    </div>
                  </div>
                </>
              )}

              {reportType === 'sub_qr' && (
                <>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>รหัส QR Active คลังย่อย</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#0284c7' }}>
                      {subQrReportData.totalTags} <span style={{ fontSize: '0.65em', fontWeight: 'normal' }}>แท็ก</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>รายการวัสดุที่ไม่ซ้ำ</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#059669' }}>
                      {subQrReportData.uniqueSKUs} <span style={{ fontSize: '0.65em', fontWeight: 'normal' }}>SKUs</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75em', color: '#64748b' }}>มูลค่าของที่ติดแท็กในคลังย่อย</div>
                    <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#d97706' }}>
                      ฿{subQrReportData.totalTaggedValuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* 3. Main Data Table */}
            <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
              {/* TABLE 1: Inventory Balance & Valuation */}
              {reportType === 'balance' && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8em', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                      <th style={{ padding: '8px 6px', border: '1px solid #cbd5e1', textAlign: 'center', width: '40px' }}>ลำดับ</th>
                      <th style={{ padding: '8px 10px', border: '1px solid #cbd5e1' }}>รายการวัสดุสิ้นเปลือง</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1' }}>สาขา</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>ล็อต (Lot)</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>วันหมดอายุ</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'right' }}>คงเหลือ</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>หน่วย</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'right' }}>ราคา/หน่วย</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'right' }}>มูลค่ารวม (บาท)</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>สถานะความปลอดภัย</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceReportData.rows.length === 0 ? (
                      <tr>
                        <td colSpan="10" style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                          ไม่พบข้อมูลวัสดุสิ้นเปลืองตามเงื่อนไขที่ระบุ
                        </td>
                      </tr>
                    ) : (
                      balanceReportData.rows.map((row, idx) => (
                        <tr key={row.id || idx} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                          <td style={{ padding: '6px 4px', border: '1px solid #e2e8f0', textAlign: 'center' }}>{idx + 1}</td>
                          <td style={{ padding: '6px 10px', border: '1px solid #e2e8f0', fontWeight: '600', color: '#0f172a' }}>
                            {row.item_name}
                            {row.isBelowMin && (
                              <span style={{ fontSize: '0.75em', color: '#dc2626', marginLeft: '6px' }}>⚠️ ต่ำกว่าเกณฑ์</span>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#475569' }}>{row.specialty}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'center', fontFamily: 'monospace' }}>{row.lot_number}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>{row.exp_date}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '700', color: row.quantity === 0 ? '#dc2626' : '#0f172a' }}>
                            {row.quantity.toLocaleString()}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'center', color: '#475569' }}>{row.unit}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'right' }}>{row.unit_price.toFixed(2)}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '600' }}>
                            {row.total_valuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.85em',
                                fontWeight: '700',
                                backgroundColor: row.statusBg,
                                color: row.statusColor,
                                border: `1px solid ${row.statusColor}33`
                              }}
                            >
                              {row.statusLabel}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {balanceReportData.rows.length > 0 && (
                    <tfoot>
                      <tr style={{ background: '#e2e8f0', fontWeight: '800', borderTop: '2px solid #94a3b8' }}>
                        <td colSpan="5" style={{ padding: '10px 12px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                          ยอดรวมทั้งสิ้น ({balanceReportData.totalSKUs} รายการ):
                        </td>
                        <td style={{ padding: '10px 8px', border: '1px solid #cbd5e1', textAlign: 'right', color: '#059669', fontSize: '1.05em' }}>
                          {balanceReportData.sumQty.toLocaleString()}
                        </td>
                        <td style={{ padding: '10px 8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>ชิ้น</td>
                        <td style={{ padding: '10px 8px', border: '1px solid #cbd5e1' }}></td>
                        <td style={{ padding: '10px 8px', border: '1px solid #cbd5e1', textAlign: 'right', color: '#0369a1', fontSize: '1.05em' }}>
                          ฿{balanceReportData.sumValuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '10px 8px', border: '1px solid #cbd5e1' }}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}

              {/* TABLE 2: Stock Movement & Audit */}
              {reportType === 'movement' && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8em', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                      <th style={{ padding: '8px 6px', border: '1px solid #cbd5e1', textAlign: 'center', width: '40px' }}>ลำดับ</th>
                      <th style={{ padding: '8px 10px', border: '1px solid #cbd5e1', width: '140px' }}>วันที่ - เวลา</th>
                      <th style={{ padding: '8px 10px', border: '1px solid #cbd5e1', width: '130px' }}>ประเภทรายการ</th>
                      <th style={{ padding: '8px 10px', border: '1px solid #cbd5e1' }}>รายการวัสดุ</th>
                      <th style={{ padding: '8px 10px', border: '1px solid #cbd5e1' }}>คลัง / สถานที่</th>
                      <th style={{ padding: '8px 10px', border: '1px solid #cbd5e1' }}>รายละเอียดการทำรายการ</th>
                      <th style={{ padding: '8px 10px', border: '1px solid #cbd5e1', width: '120px' }}>ผู้ทำรายการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movementReportData.rows.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                          ไม่พบประวัติความเคลื่อนไหวในช่วงเวลาที่ระบุ
                        </td>
                      </tr>
                    ) : (
                      movementReportData.rows.map((row, idx) => (
                        <tr key={row.id || idx} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                          <td style={{ padding: '6px 4px', border: '1px solid #e2e8f0', textAlign: 'center' }}>{idx + 1}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#475569', fontSize: '0.9em' }}>
                            {row.timestamp ? new Date(row.timestamp).toLocaleString('th-TH', { month: 'short', day: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.82em',
                                fontWeight: '700',
                                backgroundColor: row.typeBg,
                                color: row.typeColor
                              }}
                            >
                              {row.typeLabel}
                            </span>
                          </td>
                          <td style={{ padding: '6px 10px', border: '1px solid #e2e8f0', fontWeight: '600' }}>{row.item_name}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#475569' }}>{row.warehouse}</td>
                          <td style={{ padding: '6px 10px', border: '1px solid #e2e8f0', color: '#1e293b' }}>{row.action}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#0369a1', fontWeight: '600' }}>{row.actor}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {movementReportData.rows.length > 0 && (
                    <tfoot>
                      <tr style={{ background: '#e2e8f0', fontWeight: '800', borderTop: '2px solid #94a3b8' }}>
                        <td colSpan="7" style={{ padding: '10px 14px', border: '1px solid #cbd5e1' }}>
                          รวมประวัติการทำรายการ: {movementReportData.totalMovements} รายการ (รับเข้า {movementReportData.inCount}, โอนย้าย {movementReportData.transferCount}, เบิกจ่าย {movementReportData.outCount})
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}

              {/* TABLE 3: Sub-Warehouse QR Tag Status */}
              {reportType === 'sub_qr' && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8em', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                      <th style={{ padding: '8px 6px', border: '1px solid #cbd5e1', textAlign: 'center', width: '40px' }}>ลำดับ</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'center', width: '130px' }}>รหัส QR Code</th>
                      <th style={{ padding: '8px 10px', border: '1px solid #cbd5e1' }}>ชื่อรายการวัสดุ</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>ล็อต (Lot)</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1' }}>สาขา</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>วันหมดอายุ</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'right' }}>ราคา (บาท)</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>สถานะแท็ก</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1' }}>สถานที่</th>
                      <th style={{ padding: '8px 8px', border: '1px solid #cbd5e1' }}>ผู้ดูแล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoadingQr ? (
                      <tr>
                        <td colSpan="10" style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                          กำลังโหลดข้อมูลแท็ก QR Code คลังย่อย...
                        </td>
                      </tr>
                    ) : subQrReportData.rows.length === 0 ? (
                      <tr>
                        <td colSpan="10" style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                          ยังไม่มีรายการวัสดุที่ติดแท็ก QR Code ในคลังย่อยตามเงื่อนไข
                        </td>
                      </tr>
                    ) : (
                      subQrReportData.rows.map((row, idx) => (
                        <tr key={row.id || idx} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                          <td style={{ padding: '6px 4px', border: '1px solid #e2e8f0', textAlign: 'center' }}>{idx + 1}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'center', fontFamily: 'monospace', fontWeight: '700', color: '#0369a1' }}>
                            {row.qr_code}
                          </td>
                          <td style={{ padding: '6px 10px', border: '1px solid #e2e8f0', fontWeight: '600' }}>{row.item_name}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'center', fontFamily: 'monospace' }}>{row.lot_number}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#475569' }}>{row.specialty}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>{row.exp_date}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'right' }}>{row.unit_price.toFixed(2)}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '0.82em',
                                fontWeight: '700',
                                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                                color: '#059669',
                                border: '1px solid rgba(16, 185, 129, 0.3)'
                              }}
                            >
                              {row.status === 'active' ? 'พร้อมใช้ (Active)' : row.status}
                            </span>
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#475569' }}>{row.current_location}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#0369a1' }}>{row.caretaker_name}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {subQrReportData.rows.length > 0 && (
                    <tfoot>
                      <tr style={{ background: '#e2e8f0', fontWeight: '800', borderTop: '2px solid #94a3b8' }}>
                        <td colSpan="6" style={{ padding: '10px 12px', border: '1px solid #cbd5e1', textAlign: 'right' }}>
                          ยอดรวมทั้งสิ้น ({subQrReportData.totalTags} รหัสแท็ก, {subQrReportData.uniqueSKUs} รายการไม่ซ้ำ):
                        </td>
                        <td style={{ padding: '10px 8px', border: '1px solid #cbd5e1', textAlign: 'right', color: '#0369a1', fontSize: '1.05em' }}>
                          ฿{subQrReportData.totalTaggedValuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td colSpan="3" style={{ padding: '10px 8px', border: '1px solid #cbd5e1' }}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>

            {/* 4. Official Sign-off Section (Printable) */}
            <div
              className="print-break-inside-avoid"
              style={{
                marginTop: '36px',
                paddingTop: '20px',
                borderTop: '1px dashed #cbd5e1',
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '24px',
                textAlign: 'center',
                fontSize: '0.85em',
                color: '#334155'
              }}
            >
              {/* Sign-off 1: Report Creator */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontWeight: '700', color: '#0f172a' }}>ผู้จัดทำรายงาน</div>
                <div style={{ marginTop: '28px', borderBottom: '1px dotted #64748b', width: '80%', margin: '28px auto 4px auto' }} />
                <div>( .................................................... )</div>
                <div style={{ fontSize: '0.9em', color: '#64748b' }}>
                  ตำแหน่ง: เจ้าหน้าที่งานพัสดุ / บริหารคลัง
                </div>
                <div style={{ fontSize: '0.85em', color: '#64748b' }}>
                  วันที่: ...... / ...... / ...........
                </div>
              </div>

              {/* Sign-off 2: Inspector / Auditor */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontWeight: '700', color: '#0f172a' }}>ผู้ตรวจสอบ</div>
                <div style={{ marginTop: '28px', borderBottom: '1px dotted #64748b', width: '80%', margin: '28px auto 4px auto' }} />
                <div>( .................................................... )</div>
                <div style={{ fontSize: '0.9em', color: '#64748b' }}>
                  ตำแหน่ง: หัวหน้างานพัสดุและบำรุงรักษา
                </div>
                <div style={{ fontSize: '0.85em', color: '#64748b' }}>
                  วันที่: ...... / ...... / ...........
                </div>
              </div>

              {/* Sign-off 3: Warehouse Caretaker */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontWeight: '700', color: '#0f172a' }}>ผู้ดูแลคลัง / พยาน</div>
                <div style={{ marginTop: '28px', borderBottom: '1px dotted #64748b', width: '80%', margin: '28px auto 4px auto' }} />
                <div>( .................................................... )</div>
                <div style={{ fontSize: '0.9em', color: '#64748b' }}>
                  ตำแหน่ง: ผู้ดูแลคลังย่อย / ประจำห้องฟัน
                </div>
                <div style={{ fontSize: '0.85em', color: '#64748b' }}>
                  วันที่: ...... / ...... / ...........
                </div>
              </div>
            </div>

            {/* Document Footer */}
            <div
              style={{
                marginTop: '28px',
                textAlign: 'center',
                fontSize: '0.72em',
                color: '#94a3b8',
                borderTop: '1px solid #f1f5f9',
                paddingTop: '8px'
              }}
            >
              เอกสารรายงานระบบบริหารจัดการวัสดุสิ้นเปลือง โรงพยาบาลทันตกรรม คณะทันตแพทยศาสตร์ • สร้างเมื่อ {new Date().toLocaleString('th-TH')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsumableWarehouseReportModal;
